import { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AskIcon, BoothIcon, FeedIcon, PlusIcon, SearchIcon, YouIcon } from './Icons';

// The four letters of "voto" keep the original Voto logo colours (sampled from
// public/voto/logo.png); "moto" stays in the surrounding text colour. Eric,
// 2026-10-09: "where it says voto, use the original colors for v o t o".
export const VOTO_LETTER_COLORS = ['#F03048', '#40A0C0', '#F8A840', '#98C038'];

export function VotoLetters({ moto = true }) {
  return (
    <>
      {['v', 'o', 't', 'o'].map((ch, i) => (
        <span key={i} style={{ color: VOTO_LETTER_COLORS[i] }} aria-hidden="true">{ch}</span>
      ))}
      {moto && <span aria-hidden="true">moto</span>}
    </>
  );
}

export function Wordmark({ className = 'text-xl' }) {
  return (
    <span className={`font-voto font-bold tracking-[-0.02em] text-white ${className}`} aria-label="votomoto" role="img">
      <VotoLetters />
    </span>
  );
}

function SearchForm() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  function handleSubmit(e) {
    e.preventDefault();
    const trimmed = q.trim();
    if (trimmed) navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  }

  return (
    <form onSubmit={handleSubmit} className="hidden min-w-0 max-w-xs flex-1 md:block" role="search">
      <input
        aria-label="Search polls or people"
        className="w-full rounded-lg border border-voto-line bg-voto-bg px-3 py-1.5 text-sm text-voto-fg outline-none transition placeholder:text-voto-muted focus:border-voto-coral"
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search polls or people…"
        type="search"
        value={q}
      />
    </form>
  );
}

const navLinkClass = ({ isActive }) =>
  `rounded-md px-2 py-1 text-sm font-medium transition ${isActive ? 'text-white' : 'text-voto-muted hover:text-white'}`;

export function Header({ user, loading, onLogout }) {
  return (
    <header className="sticky top-0 z-30 bg-voto-chrome">
      <div className="mx-auto flex w-full max-w-6xl items-center gap-2.5 px-4 pb-2.5 pt-3.5 sm:px-6">
        <Link aria-label="votomoto home" to="/" className="rounded-md">
          <Wordmark />
        </Link>
        <nav aria-label="Main" className="ml-3 hidden items-center gap-1 sm:flex">
          <NavLink to="/feed" className={navLinkClass}>Feed</NavLink>
          <NavLink to="/booth" className={navLinkClass}>Booth</NavLink>
          {user && <NavLink to="/credits" className={navLinkClass}>Credits</NavLink>}
        </nav>
        <span className="flex-1" />
        <SearchForm />
        <Link
          to="/search"
          aria-label="Search"
          className="flex h-9 w-9 flex-none items-center justify-center rounded-lg text-white transition hover:bg-white/10 md:hidden"
        >
          <SearchIcon />
        </Link>
        {loading && <span className="text-xs text-voto-muted">…</span>}
        {!loading && user && (
          <>
            <Link
              to={`/u/${user.handle}`}
              className="hidden max-w-[10rem] truncate rounded-md px-1 text-sm text-voto-muted transition hover:text-white sm:inline"
            >
              @{user.handle}
            </Link>
            {user.is_admin && (
              <Link to="/admin" className="hidden rounded-md px-1 text-sm text-voto-muted transition hover:text-white sm:inline">
                Admin
              </Link>
            )}
            <button
              className="hidden rounded-md px-1 text-sm text-voto-muted transition hover:text-white sm:inline"
              type="button"
              onClick={onLogout}
            >
              Sign out
            </button>
          </>
        )}
        {!loading && !user && (
          <Link to="/auth" className="flex-none rounded-lg px-2 py-1.5 text-sm font-semibold text-white transition hover:bg-white/10">
            Sign in
          </Link>
        )}
        <Link
          to="/polls/new"
          aria-label="New poll"
          className="flex h-[34px] w-[34px] flex-none items-center justify-center rounded-[10px] bg-voto-coral text-white transition hover:bg-voto-coralDeep"
        >
          <PlusIcon />
        </Link>
      </div>
    </header>
  );
}

// Phone tab bar: Feed · Booth · Ask · You (spec §1). Desktop uses the header.
export function TabBar({ user }) {
  const { pathname } = useLocation();
  const youPath = user ? `/u/${user.handle}` : '/auth';
  const tabs = [
    { label: 'Feed', to: '/feed', icon: FeedIcon, active: pathname === '/' || pathname.startsWith('/feed') },
    { label: 'Booth', to: '/booth', icon: BoothIcon, active: pathname.startsWith('/booth') },
    { label: 'Ask', to: '/polls/new', icon: AskIcon, active: pathname.startsWith('/polls/new') },
    {
      label: 'You',
      to: youPath,
      icon: YouIcon,
      active: (user && pathname === `/u/${user.handle}`) || pathname.startsWith('/credits') || pathname === '/auth',
    },
  ];
  return (
    <nav aria-label="Tabs" className="pb-safe fixed inset-x-0 bottom-0 z-30 bg-voto-chrome sm:hidden">
      <ul className="flex justify-around px-3 pb-2 pt-2">
        {tabs.map(({ label, to, icon: Icon, active }) => (
          <li key={label}>
            <Link
              to={to}
              aria-current={active ? 'page' : undefined}
              className={`flex min-w-[64px] flex-col items-center gap-0.5 rounded-lg px-2 py-1 text-xs font-medium transition ${
                active ? 'text-white' : 'text-voto-muted hover:text-white'
              }`}
            >
              <Icon className="h-5 w-5" />
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-voto-line bg-voto-bg">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-6 text-sm text-voto-muted sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <span className="font-voto text-base font-bold tracking-[-0.02em] text-voto-muted" aria-label="votomoto" role="img"><VotoLetters /></span>
        <nav aria-label="Footer" className="flex flex-wrap gap-x-5 gap-y-2">
          <Link className="rounded transition hover:text-voto-fg" to="/feed">Browse polls</Link>
          <Link className="rounded transition hover:text-voto-fg" to="/tags">Tags</Link>
          <Link className="rounded transition hover:text-voto-fg" to="/privacy">Privacy</Link>
          <Link className="rounded transition hover:text-voto-fg" to="/terms">Terms</Link>
        </nav>
      </div>
    </footer>
  );
}

// Full-screen routes skip the footer (the booth is one poll at a time).
const NO_FOOTER = ['/booth'];

export function Layout({ children, user, loading, onLogout }) {
  const { pathname } = useLocation();
  const showFooter = !NO_FOOTER.some((p) => pathname.startsWith(p));
  return (
    <div className="flex min-h-screen flex-col bg-voto-bg pb-16 sm:pb-0">
      <Header user={user} loading={loading} onLogout={onLogout} />
      <div className="flex-1">{children}</div>
      {showFooter && <Footer />}
      <TabBar user={user} />
    </div>
  );
}
